<template>
  <admin-page-layout>
    <template #titleName>
      Liste des variants du produit "{{ masterProductData?.properties?.adminProductName[0]?.value}}"
    </template>

    <template #content>
      <DataTable
        :value="tableData"
        :rows="rows"
        :loading="isLoading"
        :rowsPerPageOptions="rowsPerPageOptions"
        :globalFilterFields="['properties.adminProductName.value']"
      >
        <template #header>
          <div class="search-container">
          <span class="p-input-icon-left">
           <FormKit
             type="submit"
             label="Retour"
             @click="goBack"
             prefix-icon="left"
             :style="{ 
          borderColor: '#0275ff',
          borderWidth: '1px',
          borderStyle: 'solid',
          color: '#0275ff',
          backgroundColor: 'white',
          transition: 'background-color 0.3s, color 0.3s',
        }"
             @mouseover="$event.target.style.backgroundColor = '#0275ff'; $event.target.style.color = 'white'"
             @mouseout="$event.target.style.backgroundColor = 'white'; $event.target.style.color = '#0275ff'"
           />
          </span>
            <FormKit
              type="submit"
              label="Ajouter des variants"
              @click="router.push({ name: 'customAddAttribut', params: { uuid: route.params.uuid }})"
              :style="{ 
          borderColor: '#0275ff',
          borderWidth: '1px',
          borderStyle: 'solid',
          color: '#0275ff',
          backgroundColor: 'white',
          transition: 'background-color 0.3s, color 0.3s',
        }"
              @mouseover="$event.target.style.backgroundColor = '#0275ff'; $event.target.style.color = 'white'"
              @mouseout="$event.target.style.backgroundColor = 'white'; $event.target.style.color = '#0275ff'"
            />
          </div>
        </template>
        <div class="card-body pt-0">
          <Column
            field="properties.adminProductName.value"
            header="Nom du variant"
          >
            <template #body="slotProps">
              {{ slotProps.data.properties.adminProductName[0].value }}
            </template>
          </Column>

          <Column header="Attribut (valeur)">
            <template #body="slotProps">
              {{ stringifyProductComponentObject(slotProps.data.attributes) }}
            </template>
          </Column>
          <Column
            field="properties.brand.value"
            header="Marque"
          >
            <template #body="slotProps">
              {{ slotProps.data.properties.brand[0].value }}
            </template>
          </Column>
          <Column field="supplier.value" header="Fournisseur" />
          <Column header="Statut">
            <template #body="slotProps">
              <span>
                {{
                  formatStatus(
                    slotProps.data.status.active,
                    slotProps.data.status.hiddenProduct,
                    slotProps.data.status.closedSale
                  )
                }}
              </span>
            </template>
          </Column>
          <Column headerStyle="width: 12em;">
            <template #header>
              <div>Actions</div>
            </template>
            <template #body="slotProps">
              <router-link :to="{ name: 'EditVariantProduct', params: { uuid: slotProps.data.uuid }}">
              <Button
                size="small"
                icon="pi pi-pencil"
                class="p-button-warning"
                style="margin-right: 0.5em"
                title="Éditer">
              </Button>
              </router-link>
              <Button
                size="small"
                icon="pi pi-trash"
                class="p-button-danger"
                style="margin-right: 0.5em"
                title="Archiver"
                :disabled="slotProps.data.isDefault"
                @click="onDelete(slotProps.data.uuid)"
              >
              </Button>
            </template>
          </Column>
        </div>
        <template #empty>Ce produit n’a pas de variants.</template>
      </DataTable>
    </template>
  </admin-page-layout>
</template>

<script setup lang="ts">
import AdminPageLayout from "@/layouts/default/admin/AdminPageLayout.vue";
import { fetchProductVariants, archiveVariantProduct } from "@/_services/product/variant/productVariant";
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { ProductVariant } from "@/models/product/variant/ProductVariant";
import { ProductComponentObject } from "@/models/utility/ProductComponentObject";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import swal from "@/_helpers/swal";
import { formatStatus } from "@/_helpers/stringManipulations";
import router from "@/router";
import ApiService from "@/_services/api.service";

const productVariants = Array<ProductVariant>();
const rows = ref<number>(10);
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];
const isLoading = ref<boolean>(true);
const tableData = ref<Array<ProductVariant>>(productVariants);
const route = useRoute();
let masterProductData =ref();

onMounted(async () => {
  await fetchMasterProduct();
  await populateDatatable();
});

async function populateDatatable() {  
  const [loadingDt, tableDataDt] = await fetchProductVariants({
    masterProductUuid: route.params.uuid,
    status: 1,
    isTechnical: 0
  });
  
  isLoading.value = loadingDt.value;
  tableData.value = tableDataDt.value;
}

async function fetchMasterProduct() {
  try {
    const response = await ApiService.get(`master-product/${route.params.uuid}`);
    masterProductData.value= response.data.data
  } catch (error) {
    console.error('Error fetching master product:', error);
  }
}

const onDelete = (variantUuid: string) => {
  swal
    .confirm("Êtes vous sûr de vouloir archiver ce produit ?")
    .then((result) => {
      if (result.isConfirmed) {
        archiveVariantProduct(variantUuid).then(() => {
          swal.success("Archivé !", "Le produit a bien été archivé.");          
          populateDatatable();
        }).catch((error) => {
          if (error.response.status === 422) {
            swal.error('Vous ne pouvez pas archiver ce variant. Veuillez créer un autre variant ou archiver le produit parent.');
          } else {
            swal.error();
          }
        })
      }
    });
};

function stringifyProductComponentObject(
  attributes
): string {
  let result = "";

  attributes.forEach(attribute => {
    result += `${attribute.attributeName} (${attribute.attributeValues.value}), `;
  });

  // Remove the trailing comma and space, and return the formatted string
  return result.replace(/,\s*$/, "");
}

const goBack = () => {
  router.push({ name: 'ListProducts' });
};
</script>

<style scoped lang="scss">
.returnLink {
  @apply hover:bg-[#0275ff] hover:text-white bg-white text-[#0275ff]
    transition-all duration-300 p-5 border-[1px] border-[#0275ff] rounded-md;
}


select {
  @apply font-normal;
}
.card-body {
  @apply bg-white;
}

.addProductLink {
  @apply hover:bg-[#0275ff] hover:text-white bg-white text-[#0275ff]
  transition-all duration-300 p-5 border-[1px] border-[#0275ff] rounded-md;
}
.search-container {
  display: flex;
  justify-content: space-between;
}
</style>